/*
 * Saved Templates tab — fetch lifecycle + search state for the list. Mirrors
 * useSlideTemplates.ts's mount/abort discipline (AbortController on cleanup,
 * isMountedRef around setState). Single-select, no insert step — selecting a
 * row extracts its brand via useBrandExtractor's extractFromUrls.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { getErrorMessage, isAbortError } from "../../shared/services/connectorErrors";
import { fetchSavedBrandTemplates, logSavedBrandTemplatesError, type SavedBrandTemplate } from "../services/brandTemplatesApi";

const FETCH_FAILED_MESSAGE = "Couldn't load saved templates";

export type SavedBrandTemplatesLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; templates: readonly SavedBrandTemplate[] };

export function useSavedBrandTemplates() {
  const http = useDeckorateHttp();
  const [loadState, setLoadState] = useState<SavedBrandTemplatesLoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoadState({ status: "loading" });
      try {
        const templates = await fetchSavedBrandTemplates(http, signal ? { signal } : {});
        if (!isMountedRef.current) return;
        setLoadState({ status: "ready", templates });
      } catch (error) {
        if (isAbortError(error)) return;
        const message = getErrorMessage(error, FETCH_FAILED_MESSAGE);
        logSavedBrandTemplatesError("saved-brand-templates-fetch", message);
        if (!isMountedRef.current) return;
        setLoadState({ status: "error", message });
      }
    },
    [http],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).catch((error: unknown) =>
      logSavedBrandTemplatesError("saved-brand-templates-fetch-unhandled", getErrorMessage(error, FETCH_FAILED_MESSAGE)),
    );
    return () => controller.abort();
  }, [load]);

  const retry = useCallback(() => {
    load().catch((error: unknown) => logSavedBrandTemplatesError("saved-brand-templates-retry", getErrorMessage(error, FETCH_FAILED_MESSAGE)));
  }, [load]);

  const templates = loadState.status === "ready" ? loadState.templates : [];

  // Sorted, de-duplicated brand names for the "All brands" filter dropdown.
  const brandOptions = [...new Set(templates.map((t) => t.brand).filter((b): b is string => Boolean(b)))].sort(
    (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }),
  );

  const query = search.trim().toLowerCase();
  const filteredTemplates = templates.filter((t) => {
    if (brandFilter && t.brand !== brandFilter) return false;
    if (!query) return true;
    return t.name.toLowerCase().includes(query) || (t.brand ?? "").toLowerCase().includes(query);
  });

  return {
    loadState,
    templates,
    search,
    setSearch,
    brandFilter,
    setBrandFilter,
    brandOptions,
    filteredTemplates,
    retry,
  };
}
